<section class="d-flex flex-column gap-2" data-testid="reconciliation-page">
  <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
    <div class="d-flex flex-column gap-1">
      <h2 class="av-section-title m-0">{{ 'backoffice.reconciliation.title' | translate }}</h2>
      <p class="av-meta m-0">{{ 'backoffice.reconciliation.subtitle' | translate }}</p>
    </div>

    <button type="button" class="av-btn" (click)="reload()" data-testid="reconciliation-reload">
      <mat-icon aria-hidden="true">refresh</mat-icon>
      <span>{{ 'backoffice.common.reload' | translate }}</span>
    </button>
  </div>

  <p class="av-meta m-0" data-testid="reconciliation-scope-note">{{ 'backoffice.reconciliation.scope_note' | translate }}</p>
  <p class="av-meta m-0" data-testid="reconciliation-directory-note">{{ 'backoffice.reconciliation.directory_note' | translate }}</p>

  <div class="d-flex flex-wrap align-items-end gap-2">
    <button
      type="button"
      class="av-btn av-btn-primary"
      [disabled]="!facade.canRun()"
      (click)="runPortfolio()"
      data-testid="reconciliation-run"
    >
      {{ 'backoffice.reconciliation.run.button' | translate }}
    </button>

    @if (!facade.canRun() && !facade.isRunning()) {
      <p class="av-meta m-0" data-testid="reconciliation-run-restricted">{{ 'backoffice.reconciliation.run.restricted' | translate }}</p>
    }
  </div>

  <p class="av-meta m-0">{{ 'backoffice.reconciliation.run.hint' | translate }}</p>

  @if (facade.lastRunAlertOutcome()) {
    <div class="av-banner p-3 d-flex align-items-start gap-2" data-testid="reconciliation-alert-outcome">
      <mat-icon aria-hidden="true">campaign</mat-icon>
      <span>{{ facade.lastRunAlertOutcome() | translate }}</span>
    </div>
  }
</section>

<section class="av-card mt-3 p-4 d-flex flex-column gap-3" data-testid="reconciliation-latest">
  <h3 class="av-section-title m-0">{{ 'backoffice.reconciliation.latest.title' | translate }}</h3>

  @switch (facade.latestState()) {
    @case ('error') {
      <av-empty-state
        icon="error_outline"
        [title]="'backoffice.reconciliation.error.title' | translate"
        [message]="'backoffice.reconciliation.error.message' | translate"
      ></av-empty-state>
    }
    @case ('ready') {
      @if (facade.latestReport(); as report) {
        <div class="d-flex flex-wrap gap-4">
          <div class="d-flex flex-column gap-1">
            <span class="av-meta">{{ 'backoffice.reconciliation.latest.scope' | translate }}</span>
            <span class="cell-strong">{{ report.scope }}</span>
          </div>

          <div class="d-flex flex-column gap-1">
            <span class="av-meta">{{ 'backoffice.reconciliation.latest.ran_at' | translate }}</span>
            <span class="cell-strong" data-testid="latest-ran-at">
              {{ report.ran_at ? (report.ran_at | date: dateTimeFormat) : unresolved }}
            </span>
          </div>

          <div class="d-flex flex-column gap-1">
            <span class="av-meta">{{ 'backoffice.reconciliation.latest.ran_by' | translate }}</span>
            <span
              class="cell-strong"
              [matTooltip]="report.isScheduled ? ('backoffice.reconciliation.scheduled_hint' | translate) : ''"
              data-testid="latest-ran-by"
            >
              {{ report.isScheduled ? ('backoffice.reconciliation.scheduled_actor' | translate) : report.ran_by }}
            </span>
          </div>

          <div class="d-flex flex-column gap-1">
            <span class="av-meta">{{ 'backoffice.reconciliation.latest.checked' | translate }}</span>
            <span class="cell-strong">{{ report.checked_orgs }}</span>
          </div>

          <div class="d-flex flex-column gap-1">
            <span class="av-meta">{{ 'backoffice.reconciliation.latest.drifted' | translate }}</span>
            <span class="cell-strong" data-testid="latest-drifted">{{ report.drifted_orgs }}</span>
          </div>

          <div class="d-flex flex-column gap-1">
            <span class="av-meta">{{ 'backoffice.reconciliation.latest.critical' | translate }}</span>
            <span class="cell-strong" data-testid="latest-critical">{{ report.critical_orgs }}</span>
          </div>
        </div>

        @if (report.isTruncated) {
          <p class="av-meta m-0" data-testid="latest-truncated">{{ 'backoffice.reconciliation.latest.truncated' | translate }}</p>
        }

        <h4 class="av-section-title m-0">{{ 'backoffice.reconciliation.findings.title' | translate }}</h4>

        @if (report.findings.length === 0) {
          <p class="av-meta m-0" data-testid="latest-no-findings">{{ 'backoffice.reconciliation.findings.none' | translate }}</p>
        } @else {
          <div class="d-flex flex-column gap-3">
            @for (finding of report.findings; track finding.organization_id) {
              <div class="finding av-card p-3 d-flex flex-column gap-2" data-testid="finding-card">
                <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
                  <span class="cell-strong" data-testid="finding-organization">
                    {{ finding.displayName }}
                  </span>

                  <av-status-chip
                    [label]="finding.severityLabelKey | translate"
                    [tone]="finding.severityTone"
                  ></av-status-chip>
                </div>

                <div class="table-scroll">
                  <table class="av-table w-100">
                    <thead>
                      <tr>
                        <th scope="col">{{ 'backoffice.reconciliation.findings.component' | translate }}</th>
                        <th scope="col" class="text-end">{{ 'backoffice.reconciliation.findings.issued' | translate }}</th>
                        <th scope="col" class="text-end">{{ 'backoffice.reconciliation.findings.billed_local' | translate }}</th>
                        <th scope="col" class="text-end">{{ 'backoffice.reconciliation.findings.billed_stripe' | translate }}</th>
                        <th scope="col" class="text-end">{{ 'backoffice.reconciliation.findings.delta' | translate }}</th>
                      </tr>
                    </thead>

                    <tbody>
                      @for (component of finding.components; track component.component_type) {
                        <tr>
                          <td class="cell-strong">{{ componentLabelKey(component.component_type) | translate }}</td>
                          <td class="text-end">{{ component.issued }}</td>
                          <td class="text-end">{{ component.billed_local ?? unresolved }}</td>
                          <td class="text-end">{{ component.billed_stripe ?? unresolved }}</td>
                          <td class="text-end">{{ component.delta ?? unresolved }}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>

                <div class="d-flex flex-column gap-2">
                  @for (action of finding.actions; track action.shape) {
                    <div class="d-flex flex-column gap-1" data-testid="finding-action">
                      <span class="cell-strong">{{ shapeLabel(action.shape) | translate }}</span>
                      <span class="av-meta">{{ shapeExplanation(action.shape) | translate }}</span>

                      @if (action.action === null) {
                        <span class="av-meta" data-testid="finding-action-unactionable">
                          {{ 'backoffice.reconciliation.findings.unactionable' | translate }}
                        </span>
                      } @else {
                        <span class="av-source" data-testid="finding-action-endpoint">
                          {{ 'backoffice.reconciliation.findings.endpoint' | translate }}: {{ action.action }}
                        </span>
                      }
                    </div>
                  }
                </div>
              </div>
            }
          </div>
        }
      } @else {
        <av-empty-state
          icon="rule"
          [title]="'backoffice.reconciliation.latest.empty_title' | translate"
          [message]="'backoffice.reconciliation.latest.empty_message' | translate"
        ></av-empty-state>
      }
    }
    @default {
      <p class="av-meta m-0">{{ 'backoffice.reconciliation.latest.loading' | translate }}</p>
    }
  }
</section>

<section class="av-card mt-3 p-4 d-flex flex-column gap-3" data-testid="reconciliation-inspect">
  <div class="d-flex flex-column gap-1">
    <h3 class="av-section-title m-0">{{ 'backoffice.reconciliation.inspect.title' | translate }}</h3>
    <p class="av-meta m-0">{{ 'backoffice.reconciliation.inspect.hint' | translate }}</p>
  </div>

  <div class="d-flex flex-wrap align-items-end gap-2">
    <av-organization-picker
      [organizations]="facade.organizations()"
      [value]="facade.organizationId()"
      (valueChange)="selectOrganization($event)"
    ></av-organization-picker>

    <button
      type="button"
      class="av-btn"
      [disabled]="!facade.canRun() || !facade.hasOrganization()"
      (click)="runForOrganization()"
      data-testid="reconciliation-run-organization"
    >
      {{ 'backoffice.reconciliation.run.organization_button' | translate }}
    </button>
  </div>

  @if (!facade.hasOrganization()) {
    <av-empty-state
      icon="corporate_fare"
      [title]="'backoffice.reconciliation.inspect.empty_title' | translate"
      [message]="'backoffice.reconciliation.inspect.empty_message' | translate"
    ></av-empty-state>
  } @else {
    @switch (facade.findingState()) {
      @case ('error') {
        <av-empty-state
          icon="error_outline"
          [title]="'backoffice.reconciliation.error.title' | translate"
          [message]="'backoffice.reconciliation.error.message' | translate"
        ></av-empty-state>
      }
      @case ('ready') {
        @if (facade.finding(); as finding) {
          <div class="d-flex flex-wrap gap-4">
            <div class="d-flex flex-column gap-1">
              <span class="av-meta">{{ 'backoffice.reconciliation.inspect.severity' | translate }}</span>
              <av-status-chip
                [label]="finding.severityLabelKey | translate"
                [tone]="finding.severityTone"
              ></av-status-chip>
            </div>

            <div class="d-flex flex-column gap-1">
              <span class="av-meta">{{ 'backoffice.reconciliation.inspect.is_owned' | translate }}</span>
              <span class="cell-strong" data-testid="inspect-is-owned">
                {{ (finding.is_owned ? 'backoffice.common.yes' : 'backoffice.common.no') | translate }}
              </span>
            </div>

            <div class="d-flex flex-column gap-1">
              <span class="av-meta">{{ 'backoffice.reconciliation.inspect.stripe_readable' | translate }}</span>
              <span class="cell-strong">
                {{
                  finding.stripe_readable === null
                    ? unresolved
                    : ((finding.stripe_readable ? 'backoffice.common.yes' : 'backoffice.common.no') | translate)
                }}
              </span>
            </div>

            <div class="d-flex flex-column gap-1">
              <span class="av-meta">{{ 'backoffice.reconciliation.inspect.half_state' | translate }}</span>
              <span class="cell-strong" data-testid="inspect-half-state">{{ finding.half_state }}</span>
            </div>

            <div class="d-flex flex-column gap-1">
              <span class="av-meta">{{ 'backoffice.reconciliation.inspect.cancellation_not_applied' | translate }}</span>
              <span class="cell-strong" data-testid="inspect-cancellation-not-applied">
                {{ finding.cancellation_not_applied }}
              </span>
            </div>
          </div>

          <div class="table-scroll">
            <table class="av-table w-100" data-testid="inspect-component-table">
              <thead>
                <tr>
                  <th scope="col">{{ 'backoffice.reconciliation.findings.component' | translate }}</th>
                  <th scope="col" class="text-end">{{ 'backoffice.reconciliation.findings.issued' | translate }}</th>
                  <th scope="col" class="text-end">{{ 'backoffice.reconciliation.findings.billed_local' | translate }}</th>
                  <th scope="col" class="text-end">{{ 'backoffice.reconciliation.findings.billed_stripe' | translate }}</th>
                  <th scope="col" class="text-end">{{ 'backoffice.reconciliation.findings.delta' | translate }}</th>
                </tr>
              </thead>

              <tbody>
                @for (component of finding.components; track component.component_type) {
                  <tr>
                    <td class="cell-strong">{{ componentLabelKey(component.component_type) | translate }}</td>
                    <td class="text-end">{{ component.issued }}</td>
                    <td class="text-end">{{ component.billed_local ?? unresolved }}</td>
                    <td class="text-end">{{ component.billed_stripe ?? unresolved }}</td>
                    <td class="text-end">{{ component.delta ?? unresolved }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          @if (finding.actions.length === 0) {
            <p class="av-meta m-0" data-testid="inspect-no-shapes">{{ 'backoffice.reconciliation.findings.none' | translate }}</p>
          } @else {
            <div class="d-flex flex-column gap-2">
              @for (action of finding.actions; track action.shape) {
                <div class="d-flex flex-column gap-1" data-testid="inspect-action">
                  <span class="cell-strong">{{ shapeLabel(action.shape) | translate }}</span>
                  <span class="av-meta">{{ shapeExplanation(action.shape) | translate }}</span>

                  @if (action.action === null) {
                    <span class="av-meta" data-testid="inspect-action-unactionable">
                      {{ 'backoffice.reconciliation.findings.unactionable' | translate }}
                    </span>
                  } @else {
                    <span class="av-source" data-testid="inspect-action-endpoint">
                      {{ 'backoffice.reconciliation.findings.endpoint' | translate }}: {{ action.action }}
                    </span>
                  }
                </div>
              }
            </div>
          }
        }
      }
      @default {
        <p class="av-meta m-0">{{ 'backoffice.reconciliation.inspect.loading' | translate }}</p>
      }
    }
  }
</section>

<section class="av-card mt-3 p-4 d-flex flex-column gap-3" data-testid="reconciliation-history">
  <h3 class="av-section-title m-0">{{ 'backoffice.reconciliation.history.title' | translate }}</h3>

  @switch (facade.reportsState()) {
    @case ('error') {
      <av-empty-state
        icon="error_outline"
        [title]="'backoffice.reconciliation.error.title' | translate"
        [message]="'backoffice.reconciliation.error.message' | translate"
      ></av-empty-state>
    }
    @case ('ready') {
      @if (facade.reports().length === 0) {
        <p class="av-meta m-0" data-testid="history-empty">{{ 'backoffice.reconciliation.history.empty' | translate }}</p>
      } @else {
        <div class="table-scroll">
          <table class="av-table w-100" data-testid="report-table">
            <thead>
              <tr>
                <th scope="col">{{ 'backoffice.reconciliation.latest.ran_at' | translate }}</th>
                <th scope="col">{{ 'backoffice.reconciliation.latest.scope' | translate }}</th>
                <th scope="col">{{ 'backoffice.reconciliation.latest.ran_by' | translate }}</th>
                <th scope="col" class="text-end">{{ 'backoffice.reconciliation.latest.checked' | translate }}</th>
                <th scope="col" class="text-end">{{ 'backoffice.reconciliation.latest.drifted' | translate }}</th>
                <th scope="col" class="text-end">{{ 'backoffice.reconciliation.latest.critical' | translate }}</th>
              </tr>
            </thead>

            <tbody>
              @for (report of facade.reports(); track report.id) {
                <tr>
                  <td class="cell-muted">
                    {{ report.ran_at ? (report.ran_at | date: dateTimeFormat) : unresolved }}
                  </td>
                  <td>{{ report.scope }}</td>
                  <td
                    class="cell-muted"
                    [matTooltip]="report.isScheduled ? ('backoffice.reconciliation.scheduled_hint' | translate) : ''"
                    data-testid="report-ran-by"
                  >
                    {{ report.isScheduled ? ('backoffice.reconciliation.scheduled_actor' | translate) : report.ran_by }}
                  </td>
                  <td class="text-end">{{ report.checked_orgs }}</td>
                  <td class="text-end">{{ report.drifted_orgs }}</td>
                  <td class="text-end">{{ report.critical_orgs }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <av-paginator
          [page]="facade.reportPage()"
          [pageSize]="facade.pageSize()"
          [total]="facade.reportTotal()"
          (pageChange)="goToReportPage($event)"
          (pageSizeChange)="changePageSize($event)"
        ></av-paginator>
      }
    }
    @default {
      <p class="av-meta m-0">{{ 'backoffice.reconciliation.history.loading' | translate }}</p>
    }
  }
</section>
